import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, LineChart, Line, AreaChart, Area
} from 'recharts';
import { RefreshCw, TrendingUp, AlertTriangle, CheckCircle, Clock, Package, Wrench, Truck, ShoppingCart } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PALETTE = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#F97316', '#84CC16'];

const StatCard = ({ label, value, icon: Icon, color = 'blue', sub }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center gap-4 shadow-sm" data-testid={`stat-card-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${color}-50`}>
      <Icon className={`w-6 h-6 text-${color}-600`} />
    </div>
    <div>
      <p className="text-sm text-slate-500 font-medium">{label}</p>
      <p className="text-2xl font-bold text-slate-900">{value ?? '—'}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const SectionTitle = ({ icon: Icon, title, color }) => (
  <div className="flex items-center gap-2 mb-4 mt-8">
    <Icon className={`w-5 h-5 text-${color}-600`} />
    <h2 className="text-base font-semibold text-slate-800">{title}</h2>
  </div>
);

const ChartCard = ({ title, children, testId }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid={testId}>
    <h3 className="text-sm font-semibold text-slate-700 mb-4">{title}</h3>
    {children}
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-md text-xs">
        <p className="font-medium text-slate-700 mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }}>{p.name}: {typeof p.value === 'number' ? p.value.toLocaleString() : p.value}</p>
        ))}
      </div>
    );
  }
  return null;
};

const Analytics = ({ user }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/analytics/overview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
      setLastRefreshed(new Date());
    } catch {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const hasModule = (mod) => {
    if (!data) return false;
    const permKey = mod === 'procurement' ? 'purchase_orders' : mod;
    if (user?.is_admin || user?.is_super_admin) return !!data[mod];
    return !!(user?.permissions?.[permKey]?.view && data[mod]);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  const statusColors = {
    pending: '#94A3B8', 'in-progress': '#3B82F6', completed: '#10B981',
    blocked: '#EF4444', 'order-placed': '#F59E0B', 'in-transit': '#06B6D4',
    open: '#3B82F6', cancelled: '#9CA3AF'
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Operations Analytics</h1>
          <p className="text-sm text-slate-500 mt-0.5">Comprehensive view across all modules</p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors shadow-sm"
          data-testid="refresh-analytics"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh {lastRefreshed && `· ${lastRefreshed.toLocaleTimeString()}`}
        </button>
      </div>

      {/* ── TASKS ──────────────────────────────────────────────── */}
      {hasModule('tasks') && (
        <>
          <SectionTitle icon={CheckCircle} title="Task Analytics" color="blue" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <StatCard label="Total Tasks" value={data.tasks.total} icon={CheckCircle} color="blue" />
            <StatCard label="Completion Rate" value={`${data.tasks.completion_rate}%`} icon={TrendingUp} color="green" />
            <StatCard label="Last 7 Days" value={data.tasks.recent_7days} icon={Clock} color="orange" sub="newly created" />
            <StatCard label="Pending" value={data.tasks.by_status?.pending ?? 0} icon={AlertTriangle} color="yellow" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <ChartCard title="Tasks by Status" testId="chart-tasks-status">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={Object.entries(data.tasks.by_status || {}).map(([k, v]) => ({ name: k, value: v }))} cx="50%" cy="50%" outerRadius={75} dataKey="value" label={({ name, value }) => value > 0 ? `${name}: ${value}` : null}>
                    {Object.keys(data.tasks.by_status || {}).map((k, i) => (
                      <Cell key={i} fill={statusColors[k] || PALETTE[i % PALETTE.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Tasks by Priority" testId="chart-tasks-priority">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={Object.entries(data.tasks.by_priority || {}).map(([k, v]) => ({ name: k, value: v }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" fill="#3B82F6" radius={[4, 4, 0, 0]}>
                    {['Critical', 'High', 'Medium', 'Low'].map((k, i) => (
                      <Cell key={i} fill={['#EF4444', '#F59E0B', '#3B82F6', '#94A3B8'][i]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Monthly Task Creation (Last 6 months)" testId="chart-tasks-monthly">
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={data.tasks.monthly_created || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="count" fill="#BFDBFE" stroke="#3B82F6" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        </>
      )}

      {/* ── PRODUCTION ───────────────────────────────────────── */}
      {hasModule('production') && (
        <>
          <SectionTitle icon={Truck} title="Production Analytics" color="orange" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <StatCard label="Total Trips" value={data.production.total_trips} icon={Truck} color="orange" />
            <StatCard label="Total Volume (CuM)" value={data.production.total_volume_cum?.toLocaleString()} icon={TrendingUp} color="green" />
            <StatCard label="Active Equipment" value={data.production.top_equipment?.length ?? 0} icon={Truck} color="blue" sub="in use" />
            <StatCard label="Material Types" value={data.production.by_material?.length ?? 0} icon={Package} color="purple" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <ChartCard title="Monthly Volume (CuM)" testId="chart-production-monthly">
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={data.production.monthly_volume || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="volume" fill="#FED7AA" stroke="#F97316" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Top 5 Equipment by Trips" testId="chart-production-equipment">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.production.top_equipment || []} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis dataKey="door_no" type="category" tick={{ fontSize: 10 }} width={60} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="trips" fill="#F97316" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        </>
      )}

      {/* ── MAINTENANCE ──────────────────────────────────────── */}
      {hasModule('maintenance') && (
        <>
          <SectionTitle icon={Wrench} title="Maintenance Analytics" color="red" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <StatCard label="Total Logs" value={data.maintenance.total_logs} icon={Wrench} color="red" />
            <StatCard label="Open Issues" value={data.maintenance.open_count} icon={AlertTriangle} color="orange" />
            <StatCard label="Completed" value={data.maintenance.completed_count} icon={CheckCircle} color="green" />
            <StatCard label="Sections" value={data.maintenance.by_section?.length ?? 0} icon={Truck} color="blue" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <ChartCard title="By Status" testId="chart-maintenance-status">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={Object.entries(data.maintenance.by_status || {}).map(([k, v]) => ({ name: k, value: v }))} cx="50%" cy="50%" outerRadius={75} dataKey="value">
                    {Object.keys(data.maintenance.by_status || {}).map((_, i) => (
                      <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="By Section" testId="chart-maintenance-section">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.maintenance.by_section || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" fill="#EF4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Monthly Maintenance Logs" testId="chart-maintenance-monthly">
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={data.maintenance.monthly_logs || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="count" fill="#FECACA" stroke="#EF4444" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        </>
      )}

      {/* ── INVENTORY ────────────────────────────────────────── */}
      {hasModule('inventory') && (
        <>
          <SectionTitle icon={Package} title="Inventory Analytics" color="purple" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <StatCard label="Total SKUs" value={data.inventory.total_skus} icon={Package} color="purple" />
            <StatCard label="Low Stock Items" value={data.inventory.low_stock_count} icon={AlertTriangle} color="red" sub="need reorder" />
            <StatCard label="Stock Value (₹)" value={`₹${(data.inventory.total_stock_value || 0).toLocaleString()}`} icon={TrendingUp} color="green" />
            <StatCard label="Monthly Issues" value={data.inventory.monthly_issues?.slice(-1)?.[0]?.quantity ?? 0} icon={Clock} color="blue" sub="last month" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <ChartCard title="Monthly Parts Issued (Qty)" testId="chart-inventory-issues">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.inventory.monthly_issues || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="quantity" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
            {data.inventory.low_stock_items?.length > 0 && (
              <ChartCard title="Low Stock Alert" testId="chart-inventory-lowstock">
                <div className="space-y-2 max-h-48 overflow-auto">
                  {data.inventory.low_stock_items.map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-red-50 rounded-lg">
                      <span className="text-xs font-medium text-slate-700 truncate max-w-[60%]">{item.name}</span>
                      <span className="text-xs font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded-full">Qty: {item.qty}</span>
                    </div>
                  ))}
                </div>
              </ChartCard>
            )}
          </div>
        </>
      )}

      {/* ── ASSETS ───────────────────────────────────────────── */}
      {hasModule('assets') && (
        <>
          <SectionTitle icon={Truck} title="Asset Analytics" color="teal" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <StatCard label="Total Assets" value={data.assets.total_assets} icon={Truck} color="teal" />
            <StatCard label="Categories" value={data.assets.by_category?.length ?? 0} icon={Truck} color="blue" />
            <StatCard label="Sites" value={data.assets.by_site?.length ?? 0} icon={TrendingUp} color="green" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <ChartCard title="Assets by Category" testId="chart-assets-category">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={data.assets.by_category || []} cx="50%" cy="50%" outerRadius={75} dataKey="value" nameKey="name" label={({ name, value }) => `${name}: ${value}`}>
                    {(data.assets.by_category || []).map((_, i) => (
                      <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Assets by Site" testId="chart-assets-site">
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={data.assets.by_site || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" fill="#14B8A6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        </>
      )}

      {/* ── PROCUREMENT ──────────────────────────────────────── */}
      {hasModule('procurement') && (
        <>
          <SectionTitle icon={ShoppingCart} title="Procurement Analytics" color="indigo" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <StatCard label="Total PRs" value={data.procurement.total_prs} icon={ShoppingCart} color="indigo" />
            <StatCard label="Total PO Value (₹)" value={`₹${(data.procurement.total_po_value || 0).toLocaleString()}`} icon={TrendingUp} color="green" />
            <StatCard label="Pending" value={data.procurement.by_status?.pending ?? 0} icon={Clock} color="yellow" />
            <StatCard label="Issued" value={data.procurement.by_status?.issued ?? 0} icon={CheckCircle} color="green" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <ChartCard title="PR Status Distribution" testId="chart-procurement-status">
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={Object.entries(data.procurement.by_status || {}).map(([k, v]) => ({ name: k, value: v })).filter(d => d.value > 0)} cx="50%" cy="50%" outerRadius={75} dataKey="value">
                    {Object.keys(data.procurement.by_status || {}).map((_, i) => (
                      <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>
            <ChartCard title="Monthly Purchase Requisitions" testId="chart-procurement-monthly">
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={data.procurement.monthly_prs || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="count" fill="#C7D2FE" stroke="#6366F1" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        </>
      )}

      {!data && (
        <div className="text-center py-20 text-slate-400">No analytics data available</div>
      )}
    </div>
  );
};

export default Analytics;
